import { styled } from "@stitches/react";
import { Typeahead, Button, Avatar, Modal } from "@/vendor/Forge/dist";
import { ChangeEvent, useEffect } from "react";
import { useState } from "react";
import { BannerTypes, TaoUsersList } from "@/types/tao-routing";
import { useTaoUsers } from "@/hooks/use-tao-routing";
import Loader from "../Loader";
import { TaoUser } from "@/types/tao-routing";
import { useUpdateUser } from "@/hooks/use-tao-update-user-permissions";
import {
  useOverallTaoHistory,
  useTaoHistoryById,
} from "@/hooks/use-tao-history";
import { OverallTaoHistoryTable } from "../TaoHistoryTable";

const UserItemWrapper = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "5px",

  "& .fe_c_button--secondary": {
    width: "105px",
    height: "28px",
    padding: "5px",
  },
  "& .fe_c_button__content": {
    fontSize: "14px",
    fontWeight: "400",
  },
});

const UserSettingsConfirmationModalContent = styled("div", {
  marginTop: "20px",
});

const UserWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const UserTextWrapper = styled("div", {
  display: "flex",
  flexFlow: "column",
  marginLeft: "25px",

  "& p": {
    marginBottom: "0px",
    color: "Black",
  },
});

const TaoUserManagerWrapper = styled("div", {
  marginTop: "20px",

  "& p": {
    color: "#5D5E5E",
    fontSize: "14px",
  },
  "& h2": {
    fontSize: "18px",
    fontWeight: "600",
  },
});

interface Props {
  setBannerElement: (value: JSX.Element) => void;
  allBanners: BannerTypes[];
}

interface HistoryByIdProps {
  selectedTaoId: number;
}

const TaoUserManager = ({ allBanners, setBannerElement }: Props) => {
  const { data: taoUserData, isPending: isTaoUserDataPending } = useTaoUsers();
  const [searchSelectValue, setSearchSelectValue] = useState("");
  const [
    taoUserManagementConfirmationModal,
    setTaoUserManagementConfirmationModal,
  ] = useState(false);
  const [taoUserManagementRemovalModal, setTaoUserManagementRemovalModal] =
    useState(false);
  const [filteredList, setFilteredList] = useState<TaoUsersList[]>([]);
  const [selectedUser, setSelectedUser] = useState<TaoUser>({
    userName: "",
    practiceId: "",
    isTaoAdmin: true,
  });
  const { updateUser, isPending } = useUpdateUser();

  const removeUserConfirmationBanner = () => {
    setTaoUserManagementRemovalModal(false);
    return setBannerElement(allBanners[2].banner);
  };

  const handleAddUserClick = (user: TaoUser) => {
    setTaoUserManagementConfirmationModal(true);
    setSelectedUser(user);
  };

  const handleRemoveUserClick = (user: TaoUser) => {
    setTaoUserManagementRemovalModal(true);
    setSelectedUser(user);
  };

  useEffect(() => {
    if (taoUserData) {
      setFilteredList(
        taoUserData?.filter((user) =>
          user.userName.includes(searchSelectValue),
        ),
      );
    }
  }, [searchSelectValue, taoUserData]);

  return (
    <Loader
      displayText="Loading..."
      isLoading={isTaoUserDataPending || isPending}
    >
      <TaoUserManagerWrapper>
        <p>Search Users</p>
        <Typeahead
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchSelectValue(e.target.value);
          }}
          value={searchSelectValue}
          placeholder="Search"
        />
        <h2>Users</h2>
        {filteredList?.map((user, index) => (
          <UserItemWrapper key={index}>
            <UserWrapper>
              <Avatar initials={`${user.userName[0]}${user.userName[1]}`} />
              <UserTextWrapper>
                <p>{user.userName}</p>
              </UserTextWrapper>
            </UserWrapper>
            {!user.isTaoAdmin ? (
              <Button
                variant="secondary"
                onClick={() => handleAddUserClick(user)}
              >
                Add Access
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => handleRemoveUserClick(user)}
              >
                Remove Access
              </Button>
            )}
          </UserItemWrapper>
        ))}
        <Modal
          data-testid="user-manager-confirmation-modal"
          headerText={`Are you sure you want to add access for ${selectedUser?.userName}?`}
          show={taoUserManagementConfirmationModal}
          onHide={() => {
            setTaoUserManagementConfirmationModal(false);
            setSearchSelectValue("");
          }}
          onPrimaryClick={() => {
            updateUser({
              userName: selectedUser?.userName,
              practiceId: selectedUser?.practiceId,
              isTaoAdmin: true,
            });
            setTaoUserManagementConfirmationModal(false);
            setSearchSelectValue("");
          }}
          primaryButtonText="Add"
          secondaryButtonText="Cancel"
          alertType="attention"
        >
          <UserSettingsConfirmationModalContent>
            <p>
              This user will gain access to creating, editing, or duplicating
              TAOs. They will also gain the ability to create a bucket. They
              will still be able to view TAOs.
            </p>
          </UserSettingsConfirmationModalContent>
        </Modal>
        <Modal
          data-testid="user-manager-removal-modal"
          headerText={`Are you sure you want to remove access for ${selectedUser?.userName}?`}
          show={taoUserManagementRemovalModal}
          onHide={() => {
            setTaoUserManagementRemovalModal(false);
            setSearchSelectValue("");
          }}
          onPrimaryClick={() => {
            updateUser({
              userName: selectedUser?.userName,
              practiceId: selectedUser?.practiceId,
              isTaoAdmin: false,
            });
            setTaoUserManagementRemovalModal(false);
            setSearchSelectValue("");
            removeUserConfirmationBanner();
          }}
          primaryButtonText="Remove"
          secondaryButtonText="Cancel"
          alertType="attention"
        >
          <UserSettingsConfirmationModalContent>
            <p>
              This user will lose access to creating, editing, or duplicating
              TAOs. They will also lose the ability to create a bucket. They
              will still be able to view TAOs.
            </p>
          </UserSettingsConfirmationModalContent>
        </Modal>
      </TaoUserManagerWrapper>
    </Loader>
  );
};

const TaoHistoryManager = () => {
  const { data: taoHistoryResponse, isPending: isOverallHistoryPending } =
    useOverallTaoHistory();
  const taoHistoryDetails = taoHistoryResponse?.taoHistoryDetails
    ? taoHistoryResponse?.taoHistoryDetails
    : [];
  const totalCount = taoHistoryResponse?.totalCount;
  const totalPages = taoHistoryResponse?.totalPages;

  return (
    <Loader
      isLoading={isOverallHistoryPending}
      displayText="Fetching the overall History Log"
    >
      <OverallTaoHistoryTable
        taoHistoryDetails={taoHistoryDetails}
        totalCount={totalCount}
        totalPages={totalPages}
      />
    </Loader>
  );
};

const TaoHistoryByIdManager = ({ selectedTaoId }: HistoryByIdProps) => {
  const { data: taoHistoryByIdResponse, isPending: isTaoHistoryByIdPending } =
    useTaoHistoryById(selectedTaoId);
  const taoHistoryByIdDetails = taoHistoryByIdResponse?.taoHistoryDetails
    ? taoHistoryByIdResponse?.taoHistoryDetails
    : [];
  const totalCount = taoHistoryByIdResponse?.totalCount;
  const totalPages = taoHistoryByIdResponse?.totalPages;

  return (
    <Loader
      isLoading={isTaoHistoryByIdPending}
      displayText={`Fetching the Tao ${selectedTaoId} History Log`}
    >
      <OverallTaoHistoryTable
        taoHistoryDetails={taoHistoryByIdDetails}
        totalCount={totalCount}
        totalPages={totalPages}
      />
    </Loader>
  );
};

export { TaoUserManager, TaoHistoryManager, TaoHistoryByIdManager };
